import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import DefaultLoadingSpinner from '../custom-ui/loading-spinners/default-loading-spinner'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-primary-500 text-primary-foreground hover:bg-primary-200 hover:text-secondary-900 disabled:bg-secondary-200 disabled:text-secondary-100 max-sm:w-full',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-primary bg-transparent text-primary hover:border-primary-200 hover:text-secondary-900 disabled:border-secondary-200 disabled:text-secondary-100',
        secondary: 'bg-secondary text-secondary-50 hover:bg-secondary/80',
        ghost:
          'text-primary underline hover:text-secondary-900 disabled:text-secondary-100',
        link: 'text-primary underline-offset-4 hover:underline',
        plain: '',
      },
      size: {
        default: 'h-13.5 px-6',
        icon: 'h-10 w-10',
        plain: '',
      },
      fontWeight: {
        default: ['font-medium'],
        light: 'font-light',
        normal: 'font-normal',
        medium: 'font-medium',
        semiBold: 'font-semibold',
        bold: 'font-bold',
      },
      fontSize: {
        default: 'text-base',
        sm: 'text-sm', // 14px
        base: 'text-base', // 16px
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}
export type ButtonVariant = VariantProps<typeof buttonVariants>['variant']

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {loading ? (
          <DefaultLoadingSpinner iconClassName="size-4 text-current" />
        ) : (
          children
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
